import { DriverDetailsResponse, GetHubsOfCity } from "@/central-atoms/everest-fleet-pwa/responses";
import AuthService from "@/services/api/AuthService";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Single entry point for `GET /driver_details`. Several places fire it close together (app init and
 * the driver home on first load, the flow screens right after a token is created), so only the most
 * recently dispatched request may write the store - a slower, older response must not overwrite
 * fresher data.
 */
type FetchDriverDetailsArg = { force?: boolean } | void;

const fetchDriverDetails = createAsyncThunk<DriverDetailsResponse, FetchDriverDetailsArg, { state: { driverDetails: DriverDetails } }>(
  'driver_details/fetch',
  async () => AuthService.getDriverDetails(),
  {
    /**
     * Mount fetches are deduped: AppInitializer and DriverHome both fetch on mount, and
     * React.StrictMode double-invokes each effect in dev, so the same call went out four times for
     * one screen - and driver_details fans out to several live Everest APIs, so each one is
     * expensive.
     *
     * `force: true` always runs. A refetch after the driver created a token has to reach the
     * server: driver_details can take many seconds, so a mount fetch is usually still in flight
     * when the flow finishes, and skipping the refetch left the pre-token state on screen until
     * the driver reloaded the page. Ordering is already safe - `latestRequestId` below drops any
     * response that is not from the newest request.
     */
    condition: (arg, { getState }) => {
      if (arg?.force) {
        return true;
      }

      return !getState().driverDetails.isFetching;
    },
  }
);

type DriverDetails = {
  details: DriverDetailsResponse | null,
  hubsOfCity: GetHubsOfCity | null,
  latestRequestId: string | null,
  isFetching: boolean,
}

const initialState: DriverDetails = {
  details: null,
  hubsOfCity: null,
  latestRequestId: null,
  isFetching: false,
};

const driverDetailsSlice = createSlice({
  name: "driverDetails",
  initialState,
  reducers: {
    setHubsOfCity: (state, action: PayloadAction<GetHubsOfCity>) => {
      state.hubsOfCity = action.payload;
    },
    setDriverDetails: (state, action: PayloadAction<DriverDetailsResponse>) => {
      state.details = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDriverDetails.pending, (state, action) => {
        state.latestRequestId = action.meta.requestId;
        state.isFetching = true;
      })
      .addCase(fetchDriverDetails.fulfilled, (state, action) => {
        state.isFetching = false;
        if (action.meta.requestId !== state.latestRequestId) {
          return;
        }
        state.details = action.payload;
      })
      .addCase(fetchDriverDetails.rejected, (state) => {
        state.isFetching = false;
      });
  },
});

export const { setDriverDetails, setHubsOfCity } = driverDetailsSlice.actions;
export { fetchDriverDetails };
export default driverDetailsSlice.reducer;
